import clsx from "clsx";
import styles from "./DropshadowPortalMenu.module.scss";
import { ReactNode, RefObject, useEffect, useRef } from "react";
import ReactDOM, { createPortal } from "react-dom";

type TDropshadowMenuProps = {
  isOpen: boolean;
  options: ReactNode[];
  value?: string;
  onChange?: (option: ReactNode) => void;
  anchorEl?: HTMLElement;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  width?: string;
  onClose?: () => void;
  optionClassName?: string;
  dividerIndexRate?: number | number[];
  className?: string;
  scrollContainerRef?: RefObject<HTMLElement>;
  minWidth?: string;
};

export function DropshadowPortalMenu({
  options,
  isOpen,
  anchorEl,
  top,
  left,
  right,
  bottom,
  width,
  onClose,
  className,
  dividerIndexRate,
  scrollContainerRef,
  minWidth
}: TDropshadowMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isOpen) {
        onClose?.();
      }
    }

    function handleScroll() {
      if (isOpen) {
        onClose?.();
      }
    }

    const columnsWrapper = document.getElementById("columnsWrapperId");
    document.addEventListener("mousedown", handleClickOutside);
    scrollContainerRef?.current?.addEventListener("scroll", handleScroll);
    scrollContainerRef?.current?.addEventListener("wheel", handleScroll);
    columnsWrapper?.addEventListener("scroll", handleScroll);
    columnsWrapper?.addEventListener("wheel", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      scrollContainerRef?.current?.removeEventListener("scroll", handleScroll);
      scrollContainerRef?.current?.removeEventListener("wheel", handleScroll);
      columnsWrapper?.removeEventListener("scroll", handleScroll);
      columnsWrapper?.removeEventListener("wheel", handleScroll);
    };
  }, [isOpen, onClose, scrollContainerRef]);

  useEffect(() => {
    if (isOpen && anchorEl && menuRef.current) {
      const rect = anchorEl.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(menuRef.current);
      const menuWidth = parseInt(computedStyle.width, 10);
      const menuHeight = parseInt(computedStyle.height, 10);

      let newTop = parseInt(top || "0", 10) + rect.bottom;
      let newLeft = parseInt(left || "0", 10) + rect.left;

      newTop = Math.min(newTop, window.innerHeight - menuHeight);
      newLeft = Math.min(newLeft, window.innerWidth - menuWidth);

      const style = menuRef.current.style;
      style.position = "absolute";
      style.top = `${newTop}px`;
      style.left = `${newLeft}px`;
      style.width = width ? width : "fit-content";
    }
  }, [isOpen, anchorEl, top, left, right, bottom, width]);

  const menuContent = (
    <div
      ref={menuRef}
      className={clsx(styles.container, !isOpen && styles.closedContainer, className)}
      style={{
        position: "absolute",
        top,
        left,
        right,
        bottom,
        width: width ? width : "fit-content",
        minWidth: minWidth ? minWidth : "240px"
      }}
    >
      {options?.map((option, index) => {
        function shouldShowDivider(index: number, rate?: number | number[]) {
          if (typeof rate === "number") {
            return index !== 0 && index % rate === 0;
          } else if (Array.isArray(rate)) {
            return rate.includes(index);
          }
          return false;
        }

        return (
          <div key={index}>
            {shouldShowDivider(index, dividerIndexRate) && <div className={styles.divider} />}
            {option}
          </div>
        );
      })}
    </div>
  );

  return isOpen ? createPortal(menuContent, document.body) : null;
}
